import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PreviewBanner from "@/components/PreviewBanner";
import GlobalVersionBadge from "@/components/GlobalVersionBadge";
import Login from "@/pages/Login";
import AdminLayout from "@/layouts/AdminLayout";
import CustomerLayout from "@/layouts/CustomerLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminIndex from "@/pages/admin/AdminIndex";
import TopParties from "@/pages/admin/TopParties";
import PartyAgents from "@/pages/admin/PartyAgents";
import Parties from "@/pages/admin/Parties";
import EmailLog from "@/pages/admin/EmailLog";
import ReleaseNotes from "@/pages/admin/ReleaseNotes";
import RemarkPresets from "@/pages/admin/RemarkPresets";
import Pricing from "@/pages/admin/Pricing";
import MediatorManagement from "@/pages/admin/MediatorManagement";
import MediatorCommissionReport from "@/pages/admin/MediatorCommissionReport";
import PortalManagement from "@/pages/admin/PortalManagement";
import PortalStatement from "@/pages/admin/PortalStatement";
import Transactions from "@/pages/admin/Transactions";
import Uploads from "@/pages/admin/Uploads";
import Reports from "@/pages/admin/Reports";
import OpeningClosingReport from "@/pages/admin/OpeningClosingReport";
import DayBook from "@/pages/admin/DayBook";
import RejectedSlips from "@/pages/admin/RejectedSlips";
import AgingReport from "@/pages/admin/AgingReport";
import OutstandingReport from "@/pages/admin/OutstandingReport";
import ActivityLog from "@/pages/admin/ActivityLog";
import DataMigration from "@/pages/admin/DataMigration";
import Notifications from "@/pages/admin/Notifications";
import LoginHistory from "@/pages/admin/LoginHistory";
import BroadcastMessage from "@/pages/admin/BroadcastMessage";
import Backups from "@/pages/admin/Backups";
import PaymentsByUpi from "@/pages/admin/PaymentsByUpi";
import UpiMappings from "@/pages/admin/UpiMappings";
import UpiMappingReport from "@/pages/admin/UpiMappingReport";
import TransferBalance from "@/pages/admin/TransferBalance";
import SplitPartyPeriod from "@/pages/admin/SplitPartyPeriod";
import CompanyUpis from "@/pages/admin/CompanyUpis";
import BlockedUpis from "@/pages/admin/BlockedUpis";import MonthlySummary from "@/pages/admin/MonthlySummary";
import OcrRecheck from "@/pages/admin/OcrRecheck";
import CommissionReport from "@/pages/admin/CommissionReport";
import PaymentModeReport from "@/pages/admin/PaymentModeReport";
import PaymentModesAdmin from "@/pages/admin/PaymentModesAdmin";
import SuspenseReport from "@/pages/admin/SuspenseReport";
import Leaderboard from "@/pages/admin/Leaderboard";
import StrandedCommissions from "@/pages/admin/StrandedCommissions";
import ReconcileGrid from "@/pages/admin/ReconcileGrid";
import GlobalSearch from "@/pages/admin/GlobalSearch";
import Admins from "@/pages/admin/Admins";
import DuplicateUtrs from "@/pages/admin/DuplicateUtrs";
import InvalidUtrs from "@/pages/admin/InvalidUtrs";
import PaymentCalendar from "@/pages/admin/PaymentCalendar";
import VerificationQueue from "@/pages/admin/VerificationQueue";
import SuperCompanies from "@/pages/admin/SuperCompanies";
import SuperBackupCenter from "@/pages/admin/SuperBackupCenter";
import SuperDailyArchive from "@/pages/admin/SuperDailyArchive";
import SuperDashboard from "@/pages/admin/SuperDashboard";
import TransferParty from "@/pages/admin/TransferParty";
import TraceUtr from "@/pages/admin/TraceUtr";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerLedger from "@/pages/customer/CustomerLedger";
import CustomerMonthlySummary from "@/pages/customer/MonthlySummary";
import CustomerReports from "@/pages/customer/CustomerReports";
import CustomerUpload from "@/pages/customer/CustomerUpload";
import MyUploads from "@/pages/customer/MyUploads";
import CustomerUploadsHub from "@/pages/customer/CustomerUploadsHub";
import CustomerSettings from "@/pages/customer/CustomerSettings";
import PortalAgents from "@/pages/customer/PortalAgents";
import NotificationDiag from "@/pages/customer/NotificationDiag";
import CustomerNotifications from "@/pages/customer/Notifications";
import UsersMgmt from "@/pages/admin/UsersMgmt";
import UserActivityLog from "@/pages/admin/UserActivityLog";
import SlipSummary from "@/pages/admin/SlipSummary";
import RolePermissions from "@/pages/admin/RolePermissions";
import KeyboardShortcuts from "@/pages/admin/KeyboardShortcuts";
import { ShortcutsProvider } from "@/context/ShortcutsContext";
import CashRegister from "@/pages/admin/CashRegister";
import CashExpenses from "@/pages/admin/CashExpenses";
import PartyMerge from "@/pages/admin/PartyMerge";

function App() {
    return (
        <div className="App">
            <PreviewBanner />
            <GlobalVersionBadge />
            <AuthProvider>
                <ShortcutsProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <Routes>
                        <Route path="/" element={<Navigate to="/login" replace />} />
                        <Route path="/login" element={<Login />} />

                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute role="admin">
                                    <AdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<AdminIndex />} />
                            <Route path="parties" element={<Parties />} />
                            <Route path="parties/merge" element={<PartyMerge />} />
                            <Route path="parties/transfer-balance" element={<TransferBalance />} />
                            <Route path="parties/split" element={<SplitPartyPeriod />} />
                            <Route path="party-agents" element={<PartyAgents />} />
                            <Route path="pricing" element={<Pricing />} />
                            <Route path="mediators" element={<MediatorManagement />} />
                            <Route path="portals" element={<PortalManagement />} />
                            <Route path="portals/:id/statement" element={<PortalStatement />} />
                            <Route path="transactions" element={<Transactions />} />
                            <Route path="uploads" element={<Uploads />} />
                            <Route path="rejected" element={<RejectedSlips />} />
                            <Route path="reports" element={<Reports />} />
                            <Route path="reports/day-book" element={<DayBook />} />
                            <Route path="reports/outstanding" element={<OutstandingReport />} />
                            <Route path="aging" element={<AgingReport />} />
                            <Route path="activity" element={<ActivityLog />} />
                            <Route path="migration" element={<DataMigration />} />
                            <Route path="notifications" element={<Notifications />} />
                            <Route path="login-history" element={<LoginHistory />} />
                            <Route path="broadcast" element={<BroadcastMessage />} />
                            <Route path="backups" element={<Backups />} />
                            <Route path="payments-by-upi" element={<PaymentsByUpi />} />
                            <Route path="upi-mappings" element={<UpiMappings />} />
                            <Route path="upi-mappings/:id/report" element={<UpiMappingReport />} />
                            <Route path="monthly-summary" element={<MonthlySummary />} />
                            <Route path="ocr-recheck" element={<OcrRecheck />} />
                            <Route path="reports/commission" element={<CommissionReport />} />
                            <Route path="reports/payment-modes" element={<PaymentModeReport />} />
                            <Route path="settings/payment-modes" element={<PaymentModesAdmin />} />
                            <Route path="reports/suspense" element={<SuspenseReport />} />
                            <Route path="reports/leaderboard" element={<Leaderboard />} />
                            <Route path="reports/top-parties" element={<TopParties />} />
                            <Route path="reports/opening-closing" element={<OpeningClosingReport />} />
                            <Route path="reports/stranded" element={<StrandedCommissions />} />
                            <Route path="reports/reconcile-grid" element={<ReconcileGrid />} />
                            <Route path="search" element={<GlobalSearch />} />
                            <Route path="settings/admins" element={<Admins />} />
                            <Route path="settings/role-permissions" element={<RolePermissions />} />
                            <Route path="settings/shortcuts" element={<KeyboardShortcuts />} />
                            <Route path="users" element={<UsersMgmt />} />
                            <Route path="reports/user-activity" element={<UserActivityLog />} />
                            <Route path="reports/slip-summary" element={<SlipSummary />} />
                            <Route path="reports/duplicate-utrs" element={<DuplicateUtrs />} />
                            <Route path="reports/invalid-utrs" element={<InvalidUtrs />} />
                            <Route path="reports/mediator-commission" element={<MediatorCommissionReport />} />
                            <Route path="reports/payment-calendar" element={<PaymentCalendar />} />
                            <Route path="reports/verification-queue" element={<VerificationQueue />} />
                            <Route path="super/companies" element={<SuperCompanies />} />
                            <Route path="super/backup-center" element={<SuperBackupCenter />} />
                            <Route path="super/daily-archive" element={<SuperDailyArchive />} />
                            <Route path="super/dashboard" element={<SuperDashboard />} />
                            <Route path="super/transfer-party" element={<TransferParty />} />
                            <Route path="super/trace-utr" element={<TraceUtr />} />
                            <Route path="company-upis" element={<CompanyUpis />} />
                            <Route path="blocked-upis" element={<BlockedUpis />} />
                            <Route path="email-log" element={<EmailLog />} />
                            <Route path="release-notes" element={<ReleaseNotes />} />
                            <Route path="settings/remark-presets" element={<RemarkPresets />} />
                            <Route path="cash-register" element={<CashRegister />} />
                            <Route path="cash-expenses" element={<CashExpenses />} />
                            <Route path="reports/cash-register" element={<CashRegister />} />
                        </Route>

                        <Route
                            path="/portal"
                            element={
                                <ProtectedRoute role="customer">
                                    <CustomerLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<CustomerDashboard />} />
                            <Route path="reports" element={<CustomerReports />} />
                            <Route path="uploads" element={<CustomerUploadsHub />} />
                            <Route path="ledger" element={<CustomerLedger />} />
                            <Route path="monthly-summary" element={<CustomerMonthlySummary />} />
                            <Route path="upload" element={<CustomerUpload />} />
                            <Route path="my-uploads" element={<MyUploads />} />
                            <Route path="notifications" element={<CustomerNotifications />} />
                            <Route path="settings" element={<CustomerSettings />} />
                            <Route path="agents" element={<PortalAgents />} />
                            <Route path="notifications/diag" element={<NotificationDiag />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                </BrowserRouter>
                </ShortcutsProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
